import type { PublicSeoProgram } from '@/utils/programmaticSeo';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import type { EventLandingData } from './eventLandingPublic';
import type { JourneyLandingData } from './journeyLandingPublic';
import type { ProgramLandingData } from './programLandingPublic';

/**
 * One crawlable link on the /library site-index page. Kept intentionally small
 * so it can be duplicated verbatim in api/ssr.ts (which must not import from
 * src/). Structural equality between the two definitions is what matters.
 */
export interface LibraryIndexEntry {
  /** Public path, e.g. "/p/the-summer-permission-slip" or "/j/reset-week". */
  path: string;
  title: string;
  /** Display group heading (usually a tag/category); null = ungrouped. */
  category: string | null;
  kind: 'program' | 'journey';
}

export interface LibraryIndexData {
  entries: LibraryIndexEntry[];
}

/**
 * One active Journey funnel offer as SSR resolved it (public
 * get_journey_funnel_offer row). Duplicated in api/ssr.ts; keep identical.
 */
export interface JourneyFunnelOfferSsrRow {
  offer_id: string;
  journey_id: string;
  journey_slug: string | null;
  journey_title: string;
  seo_slug: string;
  price_cents: number;
  downsell_trial_days: number;
}

export interface LiveScheduleEvent {
  id: string;
  slug: string | null;
  title: string;
  description: string | null;
  type: 'auto_run' | 'community' | 'flagship' | 'private' | 'youtube_livestream' | 'youtube';
  event_source: string | null;
  event_subtype: string | null;
  access: 'free' | 'public' | 'private' | 'premium';
  status: 'scheduled' | 'live' | 'starting_soon' | 'completed' | 'ended' | 'cancelled';
  start_at: string;
  end_at: string;
  thumbnail_url: string | null;
  youtube_url: string | null;
  youtube_video_id: string | null;
  series_key: string | null;
  is_placeholder: boolean | null;
  is_ambient_stream: boolean | null;
  created_by: string | null;
  visibility: 'public';
}

export interface LiveScheduleData {
  now: string;
  events: LiveScheduleEvent[];
}

/**
 * One program as a plain crawlable link. Duplicated in api/ssr.ts; keep
 * identical.
 */
export interface ProgramSummary {
  slug: string;
  title: string;
  description: string | null;
  promptCount: number | null;
  durationMinutes: number | null;
  mentalHealthApproved: boolean;
  /** programs.access_tier. Drives the Free / Premium badge. */
  accessTier: string;
  /** programs.free_sample. A signed-out visitor can open these. */
  freeSample: boolean;
  /** programs.thumbnail_url. Null when the row has no card image. */
  thumbnailUrl: string | null;
}

export interface ProgramListData {
  programs: ProgramSummary[];
}

export interface ProgramHubLinksData {
  hubs: Array<{ path: string; label: string }>;
  related: ProgramSummary[];
}

export interface AppSsrData {
  freeProgramsIndex?: ProgramListData;
  programsIndex?: ProgramListData;
  /** Keyed by hub path, e.g. "/journal-prompts/gratitude". */
  programsByTheme?: Record<string, ProgramSummary[]>;
  /** Keyed by /p/ slug: the program's theme hubs and related programs. */
  programHubLinksBySlug?: Record<string, ProgramHubLinksData>;
  programLandingBySlugOrId?: Record<string, ProgramLandingData | null>;
  eventLandingBySlugOrId?: Record<string, EventLandingData | null>;
  journeyLandingBySlugOrId?: Record<string, JourneyLandingData | null>;
  podcastTranscriptByNumber?: Record<string, string | null>;
  dailyPromptByDate?: Record<string, unknown>;
  libraryIndex?: LibraryIndexData;
  liveSchedule?: LiveScheduleData;
  /** Public catalog for /journal-prompts/<theme> pages (program cards in server HTML). */
  programmaticSeoPrograms?: PublicSeoProgram[];
  /** Keyed by seo slug; null means inactive or unknown (the page renders 404). */
  journeyFunnelOfferBySeoSlug?: Record<string, JourneyFunnelOfferSsrRow | null>;
  /** /programs: seo slugs of the active, sellable Journey funnels. */
  activeJourneyFunnelSeoSlugs?: string[];
  /** Raw app_config values of SSR_REMOTE_FLAG_KEYS (null: no row). Seeds the flag cache. */
  remoteFlags?: Record<string, unknown>;
}

interface BootstrappedDataResult<T> {
  data: T | null | undefined;
  hasData: boolean;
}

declare global {
  interface Window {
    __JP_SSR_DATA__?: AppSsrData;
  }
}

const AppSsrDataContext = React.createContext<AppSsrData | null>(null);
type SsrDataCarrier = typeof globalThis & {
  __JP_SSR_DATA__?: AppSsrData;
};

function hasOwn(object: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(object, key);
}

export function AppSsrDataProvider({
  children,
  data,
}: {
  children: React.ReactNode;
  data?: AppSsrData | null;
}) {
  return <AppSsrDataContext.Provider value={data ?? null}>{children}</AppSsrDataContext.Provider>;
}

function getClientSsrData(): AppSsrData | null {
  if (typeof globalThis === 'undefined') {
    return null;
  }

  return (globalThis as SsrDataCarrier).__JP_SSR_DATA__ ?? null;
}

function readHistoryKey(): string {
  try {
    const state = window.history.state as { key?: unknown } | null;
    return typeof state?.key === 'string' ? state.key : 'default';
  } catch {
    return 'default';
  }
}

// The server payload describes the page that was first loaded, and only that
// one. React Router gives each history entry its own key, so the first
// client-side navigation (including Back to the first page) retires the
// payload for the rest of the session; later routes fetch fresh data.
const initialHistoryKey = typeof window !== 'undefined' ? readHistoryKey() : null;
let navigatedAwayFromInitialPage = false;

function isStillOnInitialPage(): boolean {
  if (typeof window === 'undefined') return true;
  if (navigatedAwayFromInitialPage) return false;
  if (readHistoryKey() !== initialHistoryKey) {
    navigatedAwayFromInitialPage = true;
    delete window.__JP_SSR_DATA__;
    return false;
  }
  return true;
}

/**
 * Called by the router observer on every client-side navigation, so the
 * payload is retired even when the destination route reads no bootstrap
 * data (e.g. /library -> /about -> Back).
 */
export function retireSsrDataForSession(): void {
  navigatedAwayFromInitialPage = true;
  if (typeof window !== 'undefined') delete window.__JP_SSR_DATA__;
}

/**
 * Mount once inside the router. The first location is the server-rendered
 * page; any later location (push, replace or pop) retires the payload.
 */
export function SsrDataNavigationObserver(): null {
  const location = useLocation();
  const initialKey = React.useRef(location.key);
  React.useEffect(() => {
    if (location.key !== initialKey.current) retireSsrDataForSession();
  }, [location.key]);
  return null;
}

/** Test seam: start a fresh session. */
export function resetSsrDataNavigationForTests(): void {
  navigatedAwayFromInitialPage = false;
}

function getEffectiveSsrData(contextData: AppSsrData | null): AppSsrData | null {
  if (!isStillOnInitialPage()) return null;
  return contextData ?? getClientSsrData();
}

/**
 * True on the server and while the client is still on the page the server
 * rendered (including its hydration render). The server renders every page
 * signed out, so pages that wait for auth before showing the guest view use
 * this to render that same view during hydration instead of a loading state.
 */
export function useIsServerRenderedPage(): boolean {
  const contextData = React.useContext(AppSsrDataContext);
  return getEffectiveSsrData(contextData) !== null;
}

function useBootstrappedData<T>(
  slugOrId: string | undefined,
  getRecords: (data: AppSsrData | null) => Record<string, T | null> | undefined
): BootstrappedDataResult<T> {
  const contextData = React.useContext(AppSsrDataContext);
  const ssrData = getEffectiveSsrData(contextData);
  const records = getRecords(ssrData);

  if (!slugOrId || !records || !hasOwn(records, slugOrId)) {
    return { data: undefined, hasData: false };
  }

  return {
    data: records[slugOrId] ?? null,
    hasData: true,
  };
}

export function useBootstrappedProgramLandingData(slugOrId?: string): BootstrappedDataResult<ProgramLandingData> {
  return useBootstrappedData(slugOrId, (data) => data?.programLandingBySlugOrId);
}

export function useBootstrappedEventLandingData(slugOrId?: string): BootstrappedDataResult<EventLandingData> {
  return useBootstrappedData(slugOrId, (data) => data?.eventLandingBySlugOrId);
}

export function useBootstrappedJourneyLandingData(slugOrId?: string): BootstrappedDataResult<JourneyLandingData> {
  return useBootstrappedData(slugOrId, (data) => data?.journeyLandingBySlugOrId);
}

export function useBootstrappedJourneyFunnelOffer(seoSlug?: string): BootstrappedDataResult<JourneyFunnelOfferSsrRow> {
  return useBootstrappedData(seoSlug, (data) => data?.journeyFunnelOfferBySeoSlug);
}

export function useBootstrappedPodcastTranscript(episodeNumber?: string): BootstrappedDataResult<string> {
  return useBootstrappedData(episodeNumber, (data) => data?.podcastTranscriptByNumber);
}

export function useBootstrappedLiveSchedule(): LiveScheduleData | null {
  const contextData = React.useContext(AppSsrDataContext);
  return getEffectiveSsrData(contextData)?.liveSchedule ?? null;
}

export function useBootstrappedSeoPrograms(): PublicSeoProgram[] | null {
  const contextData = React.useContext(AppSsrDataContext);
  return getEffectiveSsrData(contextData)?.programmaticSeoPrograms ?? null;
}

export function useBootstrappedLibraryIndex(): LibraryIndexData | null {
  const contextData = React.useContext(AppSsrDataContext);
  const ssrData = getEffectiveSsrData(contextData);
  return ssrData?.libraryIndex ?? null;
}

export function useBootstrappedDailyPrompt(isoDate: string): unknown | undefined {
  const contextData = React.useContext(AppSsrDataContext);
  const ssrData = getEffectiveSsrData(contextData);
  const records = ssrData?.dailyPromptByDate;

  if (!isoDate || !records || !hasOwn(records, isoDate)) {
    return undefined;
  }

  return records[isoDate];
}

export function useBootstrappedFreeProgramsIndex(): ProgramListData | null {
  const contextData = React.useContext(AppSsrDataContext);
  return getEffectiveSsrData(contextData)?.freeProgramsIndex ?? null;
}

export function useBootstrappedProgramsIndex(): ProgramListData | null {
  const contextData = React.useContext(AppSsrDataContext);
  return getEffectiveSsrData(contextData)?.programsIndex ?? null;
}

export function useBootstrappedActiveJourneyFunnelSeoSlugs(): string[] {
  const contextData = React.useContext(AppSsrDataContext);
  return getEffectiveSsrData(contextData)?.activeJourneyFunnelSeoSlugs ?? [];
}

export function useBootstrappedProgramsByTheme(hubPath?: string): BootstrappedDataResult<ProgramSummary[]> {
  return useBootstrappedData(hubPath, (data) => data?.programsByTheme);
}

export function useBootstrappedProgramHubLinks(slug?: string): BootstrappedDataResult<ProgramHubLinksData> {
  return useBootstrappedData(slug, (data) => data?.programHubLinksBySlug);
}
